import {useState, useEffect} from 'react';
import axios from 'axios';
import {Label} from '@/components/ui/label';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import {Button} from '@/components/ui/button';
import {Check, ChevronsUpDown} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RestaurantSelector = ({
                                       value,
                                       onChange,
                                       label = 'Restaurant',
                                       required = false,
                                       allowClear = false
                                   }) => {
    const [open, setOpen] = useState(false);
    const [restaurants, setRestaurants] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);

    // ✅ Debounced Search
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchRestaurants();
        }, 400);

        return () => clearTimeout(delay);
    }, [searchQuery]);

    // ✅ Fetch list
    const fetchRestaurants = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/restaurants/search`, {
                params: {q: searchQuery}
            });
            setRestaurants(response.data || []);
        } catch (error) {
            console.error('Failed to fetch restaurants:', error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fetch selected restaurant by ID (EDIT MODE FIX)
    useEffect(() => {
        if (value) {
            fetchSelectedRestaurant(value);
        } else {
            setSelectedRestaurant(null);
        }
    }, [value]);

    const fetchSelectedRestaurant = async (id) => {
        try {
            const res = await axios.get(`${API}/restaurants/${id}`);
            setSelectedRestaurant(res.data);
        } catch (error) {
            console.error('Failed to fetch selected restaurant:', error);
        }
    };

    // ✅ Sync if already in list
    useEffect(() => {
        if (value && restaurants.length > 0) {
            const found = restaurants.find((r) => r.id === value);
            if (found) {
                setSelectedRestaurant(found);
            }
        }
    }, [value, restaurants]);

    return (
        <div>
            <Label>
                {label} {required && '*'}
            </Label>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between mt-2"
                    >
                        {selectedRestaurant?.name || 'Select restaurant...'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50"/>
                    </Button>
                </PopoverTrigger>

                <PopoverContent className="w-full p-0">
                    <Command>
                        <CommandInput
                            placeholder="Search restaurant..."
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                        />

                        <CommandEmpty>
                            {loading ? 'Loading...' : 'No restaurant found.'}
                        </CommandEmpty>

                        <CommandGroup className="max-h-64 overflow-auto">
                            {allowClear && (
                                <CommandItem
                                    key="__clear__"
                                    value="__clear__"
                                    onSelect={() => {
                                        setSelectedRestaurant(null);
                                        onChange('');
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${
                                            !selectedRestaurant ? 'opacity-100' : 'opacity-0'
                                        }`}
                                    />
                                    <div className="text-gray-500 italic">No restaurant (applies to all)</div>
                                </CommandItem>
                            )}
                            {restaurants.map((restaurant) => (
                                <CommandItem
                                    key={restaurant.id}
                                    value={restaurant.name.toLowerCase()}
                                    onSelect={() => {
                                        setSelectedRestaurant(restaurant);
                                        onChange(restaurant.id); // ✅ send ID to parent
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${
                                            selectedRestaurant?.id === restaurant.id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        }`}
                                    />

                                    <div>
                                        <div className="font-medium">{restaurant.name}</div>
                                        {restaurant.address && (
                                            <div className="text-xs text-gray-500">
                                                {restaurant.address}
                                            </div>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};